import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "consistentTaxFields",
    "treatmentTypeFields",
    "preconfiguredOption",
    "preconfiguredFields",
    "singleTaxSelect",
    "multiTaxSelect",
    "multiTaxCheckboxes",
    "manualFields",
    "manualOption",
    "exemptFields",
    "clientPreconfiguredOption",
    "clientPreconfiguredFields",
    "clientManualOption",
    "clientManualFields",
    "clientExemptFields",
    "formActions",
    "submitButton"
  ]

  static values = {
    countrySettingsUrl: String,
    showTaxTreatmentOptions: Boolean
  }

  async connect() {
    this.isInitialLoad = true
    this.supportsMultiTax = false

    await this.toggleCountry()

    if (this.selectedTreatmentType) {
      this.toggleTreatmentType()
    }

    if (this.selectedClientTreatmentType) {
      this.toggleClientTreatment()
    }

    this.isInitialLoad = false
  }

  async toggleCountry() {
    const hasCountry = this.selectedCountryCode !== ""
    const previousCountry = this.element.dataset.previousCountry
    const currentCountry = this.selectedCountryCode

    if (previousCountry && previousCountry !== currentCountry && currentCountry !== "") {
      this.resetForm()
    }

    this.element.dataset.previousCountry = currentCountry

    if (!hasCountry) {
      this.clearConsistentTaxSelection()
      if (this.hasConsistentTaxFieldsTarget) {
        this.consistentTaxFieldsTarget.hidden = true
      }
      if (this.hasTreatmentTypeFieldsTarget) {
        this.treatmentTypeFieldsTarget.hidden = true
      }
      if (this.hasFormActionsTarget) {
        this.formActionsTarget.hidden = true
      }
      this.hideAllTreatmentFields()
      this.clearClientTreatmentTypeSelection()
      this.hideAllClientTreatmentFields()
      this.validate()
      return
    }

    const hasExistingTaxTreatment = this.selectedConsistentTaxTreatment !== undefined

    if (this.showTaxTreatmentOptionsValue || hasExistingTaxTreatment) {
      if (this.hasConsistentTaxFieldsTarget) {
        this.consistentTaxFieldsTarget.hidden = false
      }

      await this.updateCountrySettings()

      const consistentTax = this.selectedConsistentTaxTreatment

      if (consistentTax === "true") {
        if (this.hasTreatmentTypeFieldsTarget) {
          this.treatmentTypeFieldsTarget.hidden = false
        }
      }

      if (this.hasFormActionsTarget) {
        this.formActionsTarget.hidden = !consistentTax
      }
    } else {
      if (this.hasConsistentTaxFieldsTarget) {
        this.consistentTaxFieldsTarget.hidden = true
      }
      if (this.hasTreatmentTypeFieldsTarget) {
        this.treatmentTypeFieldsTarget.hidden = true
      }
      if (this.hasFormActionsTarget) {
        this.formActionsTarget.hidden = false
      }
      // The client-leg treatment section (non-registered suppliers) still needs
      // the country's tax types even though the supplier treatment options are hidden
      if (this.hasClientPreconfiguredFieldsTarget) {
        await this.updateCountrySettings()
      }
    }

    this.validate()
  }

  toggleClientTreatment() {
    const selected = this.selectedClientTreatmentType

    if (this.hasClientPreconfiguredFieldsTarget) {
      this.clientPreconfiguredFieldsTarget.hidden = selected !== "preconfigured"
      if (selected !== "preconfigured" && !this.isInitialLoad) {
        this.clearFields(this.clientPreconfiguredFieldsTarget)
      }
    }

    if (this.hasClientManualFieldsTarget) {
      this.clientManualFieldsTarget.hidden = selected !== "manual"
      if (selected !== "manual" && !this.isInitialLoad) {
        this.clearFields(this.clientManualFieldsTarget)
      }
    }

    if (this.hasClientExemptFieldsTarget) {
      this.clientExemptFieldsTarget.hidden = selected !== "exempt"
      if (selected !== "exempt" && !this.isInitialLoad) {
        this.clearFields(this.clientExemptFieldsTarget)
      }
    }

    this.validate()
  }

  toggleConsistentTax() {
    const consistentTax = this.selectedConsistentTaxTreatment

    if (this.hasTreatmentTypeFieldsTarget) {
      this.treatmentTypeFieldsTarget.hidden = consistentTax !== "true"
    }

    if (consistentTax === "false") {
      this.clearTreatmentTypeSelection()
      this.hideAllTreatmentFields()
    }

    if (this.hasFormActionsTarget) {
      this.formActionsTarget.hidden = !consistentTax
    }

    this.validate()
  }

  toggleTreatmentType() {
    const selected = this.selectedTreatmentType

    if (this.hasPreconfiguredFieldsTarget) {
      this.preconfiguredFieldsTarget.hidden = selected !== "preconfigured"
      if (selected !== "preconfigured" && !this.isInitialLoad) {
        this.clearFields(this.preconfiguredFieldsTarget)
      }
    }

    if (this.hasManualFieldsTarget) {
      this.manualFieldsTarget.hidden = selected !== "manual"
      if (selected !== "manual" && !this.isInitialLoad) {
        this.clearFields(this.manualFieldsTarget)
      }
    }

    if (this.hasExemptFieldsTarget) {
      this.exemptFieldsTarget.hidden = selected !== "exempt"
      if (selected !== "exempt" && !this.isInitialLoad) {
        this.clearFields(this.exemptFieldsTarget)
      }
    }

    this.validate()
  }

  enforceMaxPercentage(event) {
    const input = event.target
    const value = parseFloat(input.value)

    if (value > 100) {
      input.value = 100
    } else if (value < 0) {
      input.value = 0
    }

    this.validate()
  }

  validate() {
    const hasCountry = this.selectedCountryCode !== ""
    let isValid = false

    if (!hasCountry) {
      isValid = false
    } else if (!this.showTaxTreatmentOptionsValue && !this.selectedConsistentTaxTreatment) {
      isValid = true
    } else {
      const consistentTax = this.selectedConsistentTaxTreatment

      if (consistentTax === "false") {
        isValid = true
      } else if (consistentTax === "true") {
        const selected = this.selectedTreatmentType

        switch (selected) {
          case "preconfigured":
            isValid = this.isPreconfiguredValid
            break
          case "manual":
            isValid = this.isManualValid
            break
          case "exempt":
            isValid = this.isExemptValid
            break
          default:
            isValid = false
        }
      }
    }

    isValid = isValid && this.isClientTreatmentValid

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !isValid
    }
  }

  clearConsistentTaxSelection() {
    const radios = this.element.querySelectorAll('input[name="job_tax_configuration[consistent_tax_treatment]"]')
    radios.forEach(radio => radio.checked = false)
  }

  clearTreatmentTypeSelection() {
    const radios = this.element.querySelectorAll('input[name="job_tax_configuration[treatment_type]"]')
    radios.forEach(radio => radio.checked = false)
  }

  clearClientTreatmentTypeSelection() {
    const radios = this.element.querySelectorAll('input[name="job_tax_configuration[client_treatment_type]"]')
    radios.forEach(radio => radio.checked = false)
  }

  resetForm() {
    this.clearConsistentTaxSelection()
    this.clearTreatmentTypeSelection()

    if (this.hasTreatmentTypeFieldsTarget) {
      this.treatmentTypeFieldsTarget.hidden = true
    }

    this.hideAllTreatmentFields()

    if (this.hasFormActionsTarget) {
      this.formActionsTarget.hidden = true
    }
  }

  hideAllTreatmentFields() {
    if (this.hasPreconfiguredFieldsTarget) {
      this.preconfiguredFieldsTarget.hidden = true
      if (!this.isInitialLoad) {
        this.clearFields(this.preconfiguredFieldsTarget)
      }
    }
    if (this.hasManualFieldsTarget) {
      this.manualFieldsTarget.hidden = true
      if (!this.isInitialLoad) {
        this.clearFields(this.manualFieldsTarget)
      }
    }
    if (this.hasExemptFieldsTarget) {
      this.exemptFieldsTarget.hidden = true
      if (!this.isInitialLoad) {
        this.clearFields(this.exemptFieldsTarget)
      }
    }
  }

  hideAllClientTreatmentFields() {
    if (this.hasClientPreconfiguredFieldsTarget) {
      this.clientPreconfiguredFieldsTarget.hidden = true
      if (!this.isInitialLoad) {
        this.clearFields(this.clientPreconfiguredFieldsTarget)
      }
    }
    if (this.hasClientManualFieldsTarget) {
      this.clientManualFieldsTarget.hidden = true
      if (!this.isInitialLoad) {
        this.clearFields(this.clientManualFieldsTarget)
      }
    }
    if (this.hasClientExemptFieldsTarget) {
      this.clientExemptFieldsTarget.hidden = true
      if (!this.isInitialLoad) {
        this.clearFields(this.clientExemptFieldsTarget)
      }
    }
  }

  clearFields(container) {
    container.querySelectorAll("input[type='text'], input[type='number'], input[type='email'], textarea").forEach(input => {
      input.value = ""
    })

    container.querySelectorAll("select").forEach(select => {
      select.selectedIndex = 0
    })

    // Uncheck multi-tax checkboxes
    container.querySelectorAll("input[type='checkbox']").forEach(checkbox => {
      checkbox.checked = false
    })
  }

  // Show the correct select variant (single vs multi) based on country
  toggleTaxSelectVariant() {
    if (this.hasSingleTaxSelectTarget) {
      this.singleTaxSelectTarget.hidden = this.supportsMultiTax
    }
    if (this.hasMultiTaxSelectTarget) {
      this.multiTaxSelectTarget.hidden = !this.supportsMultiTax
    }
  }

  // Rebuild a tax type select from JSON data, preserving the current selection
  rebuildTaxTypeSelect(select, taxTypes) {
    const currentValue = select.value
    select.innerHTML = ""

    const blankOption = document.createElement("option")
    blankOption.value = ""
    blankOption.textContent = window.I18n["general.select"]
    select.appendChild(blankOption)

    taxTypes.forEach(taxType => {
      const option = document.createElement("option")
      option.value = taxType.id
      option.textContent = taxType.label
      select.appendChild(option)
    })

    if (currentValue) {
      select.value = currentValue
    }
  }

  // Rebuild the multi-tax checkboxes from JSON data
  rebuildMultiTaxCheckboxes(taxTypes) {
    if (!this.hasMultiTaxCheckboxesTarget) return

    const container = this.multiTaxCheckboxesTarget
    container.innerHTML = ""

    taxTypes.forEach(taxType => {
      const div = document.createElement("div")
      div.className = "checkbox"

      const label = document.createElement("label")

      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.name = "job_tax_configuration[tax_type_ids][]"
      checkbox.value = taxType.id
      checkbox.dataset.action = "change->tax-configurations#validate"

      label.appendChild(checkbox)
      label.appendChild(document.createTextNode(` ${taxType.label}`))
      div.appendChild(label)
      container.appendChild(div)
    })
  }

  get selectedCountryCode() {
    const select = this.element.querySelector('select[name="job_tax_configuration[country_code]"]')
    return select ? select.value : ""
  }

  get selectedConsistentTaxTreatment() {
    return this.element.querySelector('input[name="job_tax_configuration[consistent_tax_treatment]"]:checked')?.value
  }

  get selectedTreatmentType() {
    return this.element.querySelector('input[name="job_tax_configuration[treatment_type]"]:checked')?.value
  }

  get selectedClientTreatmentType() {
    return this.element.querySelector('input[name="job_tax_configuration[client_treatment_type]"]:checked')?.value
  }

  get isPreconfiguredValid() {
    if (!this.hasPreconfiguredFieldsTarget) return false

    if (this.supportsMultiTax) {
      // At least one checkbox must be checked
      const checkboxes = this.preconfiguredFieldsTarget.querySelectorAll("input[type='checkbox']:checked")
      return checkboxes.length > 0
    } else {
      const select = this.preconfiguredFieldsTarget.querySelector("select")
      return select && select.value !== ""
    }
  }

  get isManualValid() {
    if (!this.hasManualFieldsTarget) return false
    const taxRegion = this.manualFieldsTarget.querySelector('input[name="job_tax_configuration[tax_region]"]')
    const rate = this.manualFieldsTarget.querySelector('input[type="number"]')
    return taxRegion && taxRegion.value.trim() !== "" && rate && rate.value !== ""
  }

  get isExemptValid() {
    if (!this.hasExemptFieldsTarget) return false
    const reason = this.exemptFieldsTarget.querySelector("textarea")
    return reason && reason.value.trim() !== ""
  }

  // Valid when the client-leg section is absent, standard is selected, or the
  // selected treatment's own fields are filled in
  get isClientTreatmentValid() {
    switch (this.selectedClientTreatmentType) {
      case "preconfigured": {
        if (!this.hasClientPreconfiguredFieldsTarget) return false
        const select = this.clientPreconfiguredFieldsTarget.querySelector("select")
        return !!(select && select.value !== "")
      }
      case "manual": {
        if (!this.hasClientManualFieldsTarget) return false
        const region = this.clientManualFieldsTarget.querySelector("input[type='text']")
        const rate = this.clientManualFieldsTarget.querySelector("input[type='number']")
        return !!(region && region.value.trim() !== "" && rate && rate.value !== "")
      }
      case "exempt": {
        if (!this.hasClientExemptFieldsTarget) return false
        const reason = this.clientExemptFieldsTarget.querySelector("textarea")
        return !!(reason && reason.value.trim() !== "")
      }
      default:
        return true
    }
  }

  async updateCountrySettings() {
    const country = this.selectedCountryCode

    try {
      const url = `${this.countrySettingsUrlValue}?country_code=${country}`
      const response = await fetch(url)
      const data = await response.json()

      // Update supportsMultiTax state
      this.supportsMultiTax = data.supports_multi_tax || false

      // Toggle single vs multi select
      this.toggleTaxSelectVariant()

      if (this.supportsMultiTax) {
        // Show preconfigured option — always available for multi-tax countries
        // (must be done explicitly as previous country selections may have hidden it)
        if (this.hasPreconfiguredOptionTarget) {
          this.preconfiguredOptionTarget.classList.toggle('hidden', !data.has_tax_types)
        }

        // Rebuild checkboxes from JSON (handles country change at runtime)
        if (!this.isInitialLoad) {
          this.rebuildMultiTaxCheckboxes(data.tax_types)
        }
      } else {
        // Update the single select dropdown with available tax types
        if (this.hasPreconfiguredOptionTarget) {
          this.preconfiguredOptionTarget.classList.toggle('hidden', !data.has_tax_types)

          if (this.hasPreconfiguredFieldsTarget) {
            const select = this.preconfiguredFieldsTarget.querySelector("select")
            if (select && data.tax_types && data.tax_types.length > 0) {
              this.rebuildTaxTypeSelect(select, data.tax_types)
            }
          }

          if (!this.isInitialLoad &&
              this.preconfiguredOptionTarget.classList.contains('hidden') &&
              this.selectedTreatmentType === "preconfigured") {
            this.clearTreatmentTypeSelection()
            this.hideAllTreatmentFields()
          }
        }
      }

      // Update manual option visibility (applies to all countries)
      if (this.hasManualOptionTarget) {
        this.manualOptionTarget.classList.toggle('hidden', !data.manual_rates_allowed)

        if (!this.isInitialLoad &&
            this.manualOptionTarget.classList.contains('hidden') &&
            this.selectedTreatmentType === "manual") {
          this.clearTreatmentTypeSelection()
          this.hideAllTreatmentFields()
        }
      }

      this.updateClientTreatmentOptions(data)

    } catch (error) {
      console.error('Error fetching country settings:', error)
      if (this.hasPreconfiguredOptionTarget) {
        this.preconfiguredOptionTarget.classList.add('hidden')
      }
      if (this.hasManualOptionTarget) {
        this.manualOptionTarget.classList.add('hidden')
      }
      if (this.hasClientPreconfiguredOptionTarget) {
        this.clientPreconfiguredOptionTarget.classList.add('hidden')
      }
      if (this.hasClientManualOptionTarget) {
        this.clientManualOptionTarget.classList.add('hidden')
      }
    }
  }

  // Mirrors the supplier-side option handling for the client-leg treatment section:
  // preconfigured needs the country to have tax types, manual needs manual rates
  // allowed, and the tax type select is rebuilt for the selected country.
  updateClientTreatmentOptions(data) {
    if (this.hasClientPreconfiguredOptionTarget) {
      this.clientPreconfiguredOptionTarget.classList.toggle('hidden', !data.has_tax_types)

      if (!this.isInitialLoad &&
          this.clientPreconfiguredOptionTarget.classList.contains('hidden') &&
          this.selectedClientTreatmentType === "preconfigured") {
        this.clearClientTreatmentTypeSelection()
        this.hideAllClientTreatmentFields()
      }
    }

    if (this.hasClientPreconfiguredFieldsTarget) {
      const select = this.clientPreconfiguredFieldsTarget.querySelector("select")
      if (select && data.tax_types && data.tax_types.length > 0) {
        this.rebuildTaxTypeSelect(select, data.tax_types)
      }
    }

    if (this.hasClientManualOptionTarget) {
      this.clientManualOptionTarget.classList.toggle('hidden', !data.manual_rates_allowed)

      if (!this.isInitialLoad &&
          this.clientManualOptionTarget.classList.contains('hidden') &&
          this.selectedClientTreatmentType === "manual") {
        this.clearClientTreatmentTypeSelection()
        this.hideAllClientTreatmentFields()
      }
    }
  }
}
